import { DomainError, SyncError, TransportError, } from "@econ-v1/domain";
import { KernelEventBus } from "../bus/event-bus.js";
import { LiveQueryEngine } from "../live-query/engine.js";
const CATALOG_OPERATION = "GET /api/v2/node-apps/ui-stages";
export class StageRegistryService {
    #assets;
    #bus;
    #clock;
    #fallbackRefreshMs;
    #homeNode;
    #liveQueries;
    #repository;
    #scheduler;
    #unsubscribers = [];
    #initialized = false;
    #initialization;
    #refreshPromise;
    #snapshot = { entries: [], revision: "empty" };
    constructor(dependencies, options = {}) {
        this.#assets = dependencies.assets;
        this.#bus = dependencies.bus;
        this.#clock = dependencies.clock;
        this.#fallbackRefreshMs = options.fallbackRefreshMs ?? 5 * 60_000;
        this.#homeNode = dependencies.homeNode;
        this.#liveQueries = dependencies.liveQueries;
        this.#repository = dependencies.repository;
        this.#scheduler = dependencies.scheduler;
    }
    bindSession(appName, manifestSha256) {
        const entry = this.#entry(appName);
        if (entry.manifestSha256 !== manifestSha256) {
            throw new DomainError({
                code: "not_found",
                details: { appName },
                message: "The requested stage manifest is no longer installed",
            });
        }
        return {
            appName: entry.appName,
            manifestSha256: entry.manifestSha256,
            requires: [...entry.manifest.requires],
            version: entry.manifest.version,
        };
    }
    initialize() {
        if (this.#initialization !== undefined)
            return this.#initialization;
        if (this.#initialized)
            return Promise.resolve();
        this.#registerQueries();
        this.#unsubscribers.push(this.#bus.subscribe("transport.state-changed", (event) => {
            if (event.payload.state.status === "connected")
                this.#requestRefresh();
        }), this.#bus.subscribe("stage.catalog-invalidated", () => this.#requestRefresh()));
        const dueAt = new Date(Date.parse(this.#clock.now()) + this.#fallbackRefreshMs).toISOString();
        this.#scheduler.register({
            cadenceMs: this.#fallbackRefreshMs,
            dueAt,
            name: "stage-registry.refresh",
            run: async () => {
                await this.refresh();
            },
        });
        this.#initialized = true;
        this.#initialization = this.#hydrate();
        return this.#initialization;
    }
    async #hydrate() {
        try {
            const stored = await this.#repository.read();
            if (stored !== undefined) {
                this.#snapshot = validateStoredSnapshot(stored);
                this.#publishRegistryChanged();
            }
        }
        catch {
            // A missing or corrupt cache is recoverable; the next connected refresh replaces it.
        }
        if (this.#homeNode.state.status === "connected") {
            await this.refresh().catch(() => undefined);
        }
    }
    async prepareStage(appName) {
        const entry = this.#entry(appName);
        const prepared = await this.#assets.prepare(entry.appName, entry.manifest);
        if (prepared.manifestSha256 !== entry.manifestSha256 || prepared.version !== entry.manifest.version) {
            throw new DomainError({ code: "conflict", message: "Prepared stage does not match the installed manifest" });
        }
        return prepared;
    }
    async refresh() {
        if (this.#refreshPromise !== undefined)
            return this.#refreshPromise;
        const refresh = this.#refreshOnce();
        this.#refreshPromise = refresh;
        try {
            return await refresh;
        }
        finally {
            if (this.#refreshPromise === refresh)
                this.#refreshPromise = undefined;
        }
    }
    shutdown() {
        for (const unsubscribe of this.#unsubscribers.splice(0))
            unsubscribe();
        this.#scheduler.cancel("stage-registry.refresh");
    }
    snapshot() {
        return cloneSnapshot(this.#snapshot);
    }
    async #refreshOnce() {
        let response;
        try {
            response = await this.#homeNode.invoke({ operation: CATALOG_OPERATION, payload: {} });
        }
        catch (error) {
            if (error instanceof DomainError || error instanceof SyncError || error instanceof TransportError)
                throw error;
            throw new TransportError({ code: "unavailable", message: "Stage catalog refresh failed" });
        }
        const next = parseCatalogResponse(unwrapApiData(response));
        await this.#repository.write(next);
        this.#snapshot = next;
        this.#publishRegistryChanged();
        return cloneSnapshot(next);
    }
    #entry(appName) {
        const entry = this.#snapshot.entries.find((candidate) => candidate.appName === appName);
        if (entry !== undefined)
            return entry;
        throw new DomainError({ code: "not_found", details: { appName }, message: `Stage ${appName} is not installed` });
    }
    #registerQueries() {
        // Both are `requiresDurableStorage: false` (#1661): `installed` reads this service's
        // own in-memory `#snapshot` and `transport` reads the home-node client's in-memory
        // state. Neither touches the OPFS-backed SQLite cache — the snapshot's optional
        // offline copy lives in CacheStorage via `StageRegistryPort`, and `#hydrate()`
        // already treats a missing or unreadable copy as recoverable. Keeping these two
        // available without durable storage is what lets the shell mount, route, and load
        // stages at all on a browser that has no OPFS.
        const installed = {
            invalidateOn: ["stage.registry-changed"],
            key: (entry) => entry.appName,
            name: "stages.installed.v1",
            requiresDurableStorage: false,
            run: async () => cloneSnapshot(this.#snapshot).entries,
        };
        const transport = {
            invalidateOn: ["transport.state-changed"],
            key: () => "transport",
            name: "transport.state.v1",
            requiresDurableStorage: false,
            run: async () => [this.#homeNode.state],
        };
        this.#liveQueries.register(installed);
        this.#liveQueries.register(transport);
    }
    #requestRefresh() {
        void this.refresh().catch(() => {
            // The last verified snapshot remains authoritative until a later trigger succeeds.
        });
    }
    #publishRegistryChanged() {
        this.#bus.publish({
            occurredAt: this.#clock.now(),
            payload: { appNames: this.#snapshot.entries.map((entry) => entry.appName) },
            type: "stage.registry-changed",
        });
    }
}
function cloneSnapshot(snapshot) {
    return {
        entries: snapshot.entries.map((entry) => ({
            appName: entry.appName,
            assetBase: entry.assetBase,
            manifest: {
                entry: entry.manifest.entry,
                integrity: { ...entry.manifest.integrity },
                requires: [...entry.manifest.requires],
                version: entry.manifest.version,
            },
            manifestSha256: entry.manifestSha256,
            ui: cloneUi(entry.ui),
        })),
        revision: snapshot.revision,
    };
}
function unwrapApiData(value) {
    if (isRecord(value) && "data" in value)
        return value.data;
    return value;
}
function validateStoredSnapshot(value) {
    if (!isRecord(value) || typeof value.revision !== "string" || value.revision.length === 0 || !Array.isArray(value.entries)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage registry snapshot" });
    }
    const appNames = new Set();
    const entries = value.entries.map((candidate) => validateStoredEntry(candidate, appNames));
    return cloneSnapshot({ entries, revision: value.revision });
}
function parseCatalogResponse(value) {
    if (!Array.isArray(value)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog response" });
    }
    const appNames = new Set();
    const entries = value.map((candidate) => parseCatalogEntry(candidate, appNames));
    return cloneSnapshot({
        entries,
        revision: deriveRevision(entries),
    });
}
function validateStoredEntry(value, appNames) {
    if (!isRecord(value) || !isAppName(value.appName) || appNames.has(value.appName)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog app name" });
    }
    appNames.add(value.appName);
    if (typeof value.assetBase !== "string" ||
        !isSafeAssetBase(value.assetBase, value.appName) ||
        !isSha256(value.manifestSha256) ||
        !isRecord(value.manifest)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog manifest" });
    }
    const manifest = validateManifest(value.manifest, value.appName);
    const ui = validateStoredUi(value.ui);
    ensureIntegrityForUi(manifest, ui);
    return {
        appName: value.appName,
        assetBase: value.assetBase,
        manifest,
        manifestSha256: value.manifestSha256,
        ui,
    };
}
function parseCatalogEntry(value, appNames) {
    if (!isRecord(value) || !isAppName(value.name) || appNames.has(value.name)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog app name" });
    }
    appNames.add(value.name);
    if (typeof value.version !== "string" || value.version.length === 0) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog version" });
    }
    if (!Array.isArray(value.requires) || !value.requires.every((item) => typeof item === "string" && item.length > 0)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog requirements" });
    }
    if (new Set(value.requires).size !== value.requires.length) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog requirements" });
    }
    if (!isSha256(value.manifest_sha256) || typeof value.asset_base !== "string" || !isSafeAssetBase(value.asset_base, value.name)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog identity" });
    }
    if (!isRecord(value.ui)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog ui metadata" });
    }
    const ui = parseUi(value.ui);
    const manifest = validateManifest({
        entry: value.ui.entry,
        integrity: value.ui.integrity,
        requires: value.requires,
        version: value.version,
    }, value.name);
    ensureIntegrityForUi(manifest, ui);
    return {
        appName: value.name,
        assetBase: value.asset_base,
        manifest,
        manifestSha256: value.manifest_sha256,
        ui,
    };
}
function validateManifest(value, appName) {
    if (!isRecord(value)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog manifest fields" });
    }
    if (typeof value.entry !== "string" ||
        value.entry.length === 0 ||
        typeof value.version !== "string" ||
        value.version.length === 0 ||
        !Array.isArray(value.requires) ||
        !value.requires.every((item) => typeof item === "string" && item.length > 0) ||
        new Set(value.requires).size !== value.requires.length ||
        !isRecord(value.integrity)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog manifest fields" });
    }
    const integrityEntries = Object.entries(value.integrity);
    if (integrityEntries.length === 0 ||
        !integrityEntries.every(([path, hash]) => isSafeRelativeAssetPath(path) && isSha256(hash))) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog manifest fields" });
    }
    if (!isSafeRelativeAssetPath(value.entry) || !isSha256(value.integrity[value.entry])) {
        throw new DomainError({ code: "validation_failed", message: `Invalid stage entry integrity for ${appName}` });
    }
    return {
        entry: value.entry,
        integrity: { ...value.integrity },
        requires: [...value.requires],
        version: value.version,
    };
}
function parseUi(value) {
    if ((value.kind !== "stage" && value.kind !== "widget") ||
        typeof value.title !== "string" ||
        value.title.length === 0 ||
        value.ui_api !== 1 ||
        !Array.isArray(value.composes) ||
        !value.composes.every((item) => typeof item === "string" && item.length > 0) ||
        new Set(value.composes).size !== value.composes.length) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog ui metadata" });
    }
    if (value.icon !== undefined && value.icon !== null && (typeof value.icon !== "string" || !isSafeRelativeAssetPath(value.icon))) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog ui metadata" });
    }
    if (value.kind === "widget" && value.nav !== undefined && value.nav !== null) {
        throw new DomainError({ code: "validation_failed", message: "Widget stage catalog entries must omit navigation metadata" });
    }
    const nav = value.kind === "stage" ? parseNav(value.nav) : undefined;
    return {
        composes: [...value.composes],
        ...(typeof value.icon === "string" ? { icon: value.icon } : {}),
        kind: value.kind,
        ...(nav === undefined ? {} : { nav }),
        title: value.title,
        uiApi: 1,
    };
}
function validateStoredUi(value) {
    if (!isRecord(value)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stored stage ui metadata" });
    }
    if ((value.kind !== "stage" && value.kind !== "widget") ||
        typeof value.title !== "string" ||
        value.title.length === 0 ||
        value.uiApi !== 1 ||
        !Array.isArray(value.composes) ||
        !value.composes.every((item) => typeof item === "string" && item.length > 0) ||
        new Set(value.composes).size !== value.composes.length) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stored stage ui metadata" });
    }
    if (value.kind === "widget" && value.nav !== undefined) {
        throw new DomainError({ code: "validation_failed", message: "Stored widget entries must omit navigation metadata" });
    }
    const nav = value.kind === "stage" ? validateStoredNav(value.nav) : undefined;
    if (value.icon !== undefined && (typeof value.icon !== "string" || !isSafeRelativeAssetPath(value.icon))) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stored stage ui metadata" });
    }
    return {
        composes: [...value.composes],
        ...(value.icon === undefined ? {} : { icon: value.icon }),
        kind: value.kind,
        ...(nav === undefined ? {} : { nav }),
        title: value.title,
        uiApi: 1,
    };
}
function parseNav(value) {
    if (value === undefined || value === null) {
        return { order: 0, section: "default" };
    }
    if (!isRecord(value) || typeof value.order !== "number" || !Number.isInteger(value.order)) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog nav metadata" });
    }
    const section = value.section === undefined ? "default" : value.section;
    if (typeof section !== "string" || section.length === 0) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage catalog nav metadata" });
    }
    return {
        order: value.order,
        section,
    };
}
function validateStoredNav(value) {
    if (!isRecord(value) || typeof value.order !== "number" || !Number.isInteger(value.order) || typeof value.section !== "string" || value.section.length === 0) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stored stage nav metadata" });
    }
    return {
        order: value.order,
        section: value.section,
    };
}
function ensureIntegrityForUi(manifest, ui) {
    if (ui.icon !== undefined && !isSha256(manifest.integrity[ui.icon])) {
        throw new DomainError({ code: "validation_failed", message: "Invalid stage icon integrity" });
    }
}
function cloneUi(ui) {
    return {
        composes: [...ui.composes],
        ...(ui.icon === undefined ? {} : { icon: ui.icon }),
        kind: ui.kind,
        ...(ui.nav === undefined ? {} : { nav: { order: ui.nav.order, section: ui.nav.section } }),
        title: ui.title,
        uiApi: 1,
    };
}
function deriveRevision(entries) {
    if (entries.length === 0) {
        return "empty";
    }
    return entries
        .map((entry) => `${entry.appName}@${entry.manifest.version}#${entry.manifestSha256}`)
        .join("|");
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isSha256(value) {
    return typeof value === "string" && /^[a-f0-9]{64}$/u.test(value);
}
function isSafeRelativeAssetPath(value) {
    if (typeof value !== "string" || !/^[A-Za-z0-9_][A-Za-z0-9_./-]*$/u.test(value)) {
        return false;
    }
    const segments = value.split("/");
    return segments.every((segment) => segment.length > 0 && segment !== "." && segment !== "..");
}
function isAppName(value) {
    return typeof value === "string" && /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u.test(value);
}
function isSafeAssetBase(value, appName) {
    return value === `/api/v2/node-apps/${appName}/ui/`;
}
//# sourceMappingURL=stage-registry-service.js.map